import React, {useEffect, useState} from "react";
import {Grid, TextField, Typography, Button, Link, Alert, Box} from "@mui/material";
import ProfileStyles from "../../styles/userProfile.module.css";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../../context/authContext";
import {useProfile} from "../../context/userContext";

const BillingDetails = () => {
    const navigate = useNavigate();
    const {profile} = useProfile();
    const {authenticated, token} = useAuth();
    const [userSubscriptionDetails, setuserSubscriptionDetails] = useState({});
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleRedirectToPlans = () => {
        const faaIDType = profile?.faaIDType || "Part 107";
        const planType = faaIDType === "Part 107" ? "part107" : "44809";
        navigate(`/plans?type=${planType}`);
    };

    const handleRetry = () => {
        // Force re-fetch by toggling a dependency or calling fetch directly
        if (token && profile?.stripeSubscriptionId) {
            const fetchSubscriptionDetails = async () => {
                try {
                    setLoading(true);
                    setError(null);

                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subscription`, {
                        method: "POST",
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({subscriptionId: profile.stripeSubscriptionId}),
                    });

                    if (response.status === 404) {
                        const errorData = await response.json();
                        setError({
                            type: 'notFound',
                            message: errorData.message || "Your subscription could not be found. It may have been cancelled or deleted.",
                            subscriptionId: errorData.subscriptionId
                        });
                        setuserSubscriptionDetails({});
                        return;
                    }

                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        setError({
                            type: 'serverError',
                            message: errorData.message || "There was a problem loading your subscription details. Please try again."
                        });
                        return;
                    }

                    const data = await response.json();
                    setuserSubscriptionDetails(data);
                    setError(null);
                } catch (error) {
                    setError({
                        type: 'networkError',
                        message: "Unable to connect to the server. Please check your internet connection and try again."
                    });
                } finally {
                    setLoading(false);
                }
            };

            fetchSubscriptionDetails();
        }
    };

    const handleManageSubscription = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/create-portal-session`, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({customerId: profile.stripeCustomerId}),
            });

            if (!response.ok) {
                throw new Error("Failed to create portal session");
            }

            const {url} = await response.json();
            window.location.href = url;
        } catch (error) {
            alert("An error occurred while accessing the subscription portal. Please try again.");
        }
    };

    useEffect(() => {
        const fetchSubscriptionDetails = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subscription`, {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({subscriptionId: profile.stripeSubscriptionId}),
                });

                // Handle 404 - Subscription not found
                if (response.status === 404) {
                    const errorData = await response.json();
                    setError({
                        type: 'notFound',
                        message: errorData.message || "Your subscription could not be found. It may have been cancelled or deleted.",
                        subscriptionId: errorData.subscriptionId
                    });
                    setuserSubscriptionDetails({});
                    return;
                }

                // Handle other errors (500, etc.)
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    setError({
                        type: 'serverError',
                        message: errorData.message || "There was a problem loading your subscription details. Please try again."
                    });
                    return;
                }

                const data = await response.json();
                setuserSubscriptionDetails(data);
                setError(null);
            } catch (error) {
                // Network errors or other unexpected errors
                setError({
                    type: 'networkError',
                    message: "Unable to connect to the server. Please check your internet connection and try again."
                });
            } finally {
                setLoading(false);
            }
        };

        if (token && profile?.stripeSubscriptionId) {
            fetchSubscriptionDetails();
        } else {
            setLoading(false);
        }
    }, [token, profile]);

    return (
        <div>
            <Typography className={ProfileStyles.section_header} mb={2}>
                Subscription
            </Typography>

            {/* Error Messages */}
            {error && (
                <Box mb={2}>
                    {error.type === 'notFound' && (
                        <Alert
                            severity="warning"
                            action={
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button
                                        size="small"
                                        color="inherit"
                                        onClick={handleRedirectToPlans}
                                    >
                                        View Plans
                                    </Button>
                                    <Button
                                        size="small"
                                        color="inherit"
                                        onClick={handleRetry}
                                    >
                                        Refresh
                                    </Button>
                                </Box>
                            }
                        >
                            {error.message}
                        </Alert>
                    )}
                    {error.type === 'serverError' && (
                        <Alert
                            severity="error"
                            action={
                                <Button
                                    size="small"
                                    color="inherit"
                                    onClick={handleRetry}
                                >
                                    Retry
                                </Button>
                            }
                        >
                            {error.message}
                        </Alert>
                    )}
                    {error.type === 'networkError' && (
                        <Alert
                            severity="error"
                            action={
                                <Button
                                    size="small"
                                    color="inherit"
                                    onClick={handleRetry}
                                >
                                    Retry
                                </Button>
                            }
                        >
                            {error.message}
                        </Alert>
                    )}
                </Box>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Current Plan"
                        value={loading ? "Loading..." : (userSubscriptionDetails?.items?.[0]?.productName || "No active plan")}
                        variant="outlined"
                        fullWidth
                        size="small"
                        disabled
                        sx={{ minWidth: '120px' }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Billing Period"
                        value={loading ? "Loading..." : (userSubscriptionDetails?.currentPeriodStart ?
                            `${formatDate(userSubscriptionDetails.currentPeriodStart)} - ${formatDate(userSubscriptionDetails.currentPeriodEnd)}`
                            : "N/A")}
                        variant="outlined"
                        fullWidth
                        size="small"
                        disabled
                        sx={{ minWidth: '120px' }}
                    />
                </Grid>
                {userSubscriptionDetails?.latestInvoice?.url && (
                    <Grid item xs={12}>
                        <Link
                            href={userSubscriptionDetails.latestInvoice.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ display: 'inline-block', mt: 1 }}
                        >
                            View Latest Invoice
                        </Link>
                    </Grid>
                )}
                <Grid item xs={12}>
                    {!userSubscriptionDetails?.items?.[0]?.productName ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRedirectToPlans}
                            fullWidth
                            sx={{ mt: 1 }}
                            disabled={loading}
                        >
                            Subscribe to a Plan
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleManageSubscription}
                            disabled={!profile?.stripeCustomerId || loading}
                            fullWidth
                            sx={{ mt: 1 }}
                        >
                            Manage Subscription
                        </Button>
                    )}
                </Grid>
            </Grid>
        </div>
    );
};

export default BillingDetails;